/**
 * Selection feature for @toolbox-web/grid-vue
 *
 * Import this module to enable the `selection` prop on TbwGrid.
 * Also exports `useGridSelection()` composable for programmatic selection control.
 *
 * @example
 * ```vue
 * <script setup>
 * import '@toolbox-web/grid-vue/features/selection';
 * </script>
 *
 * <template>
 *   <TbwGrid selection="range" />
 * </template>
 * ```
 *
 * @example Using the composable
 * ```vue
 * <script setup>
 * import { useGridSelection } from '@toolbox-web/grid-vue/features/selection';
 *
 * const { selectAll, clearSelection, getSelection } = useGridSelection();
 *
 * function handleSelectAll() {
 *   selectAll();
 * }
 * </script>
 * ```
 *
 * @packageDocumentation
 */

import type { DataGridElement } from '@toolbox-web/grid';
import {
  type CellRange,
  type SelectionChangeDetail,
  type SelectionPlugin,
  type SelectionResult,
} from '@toolbox-web/grid/plugins/selection';
import { inject, onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
import { GRID_ELEMENT_KEY } from '../lib/use-grid';

// Delegate to core feature registration
import '@toolbox-web/grid/features/selection';
// Named type re-export surfaces the core `FeatureConfig` augmentation to dist
// consumers — a bare side-effect import alone is stripped from the emitted
// `.d.ts`. See `.github/knowledge/adapters.md`.
export type { _Augmentation as _SelectionAugmentation } from '@toolbox-web/grid/features/selection';

/**
 * Selection methods returned from useGridSelection.
 *
 * Uses the injected grid element from TbwGrid's provide/inject.
 * Methods work immediately when grid is available.
 */
export interface SelectionMethods<TRow = unknown> {
  /**
   * Select all rows (row mode) or all cells (range mode).
   */
  selectAll: () => void;

  /**
   * Clear all selection.
   */
  clearSelection: () => void;

  /**
   * Get the current selection state.
   * Use this to derive selected rows, indices, etc.
   */
  getSelection: () => SelectionResult | null;

  /**
   * Check if a specific cell is selected.
   */
  isCellSelected: (row: number, col: number) => boolean;

  /**
   * Set selection ranges programmatically.
   */
  setRanges: (ranges: CellRange[]) => void;

  /**
   * Get actual row objects for the current selection.
   * Works in all selection modes (row, cell, range) — resolves indices
   * against the grid's processed (sorted/filtered) rows.
   *
   * This is the recommended way to get selected rows. Unlike manual
   * index mapping, it correctly resolves rows even when the grid is
   * sorted or filtered.
   *
   * For reactive selected rows, use the `selectedRows` ref instead.
   */
  getSelectedRows: () => TRow[];

  /**
   * Reactive selection state. Updates automatically whenever the selection
   * changes. `null` when no SelectionPlugin is active or nothing is selected.
   *
   * @since 2.5.0
   */
  selection: Ref<SelectionResult | null>;

  /**
   * Reactive selected row indices (sorted ascending). Empty in cell/range
   * modes or when nothing is selected.
   *
   * **Prefer `selectedRows`** for getting actual row objects — it handles
   * index-to-object resolution correctly regardless of sorting/filtering.
   *
   * @since 2.5.0
   */
  selectedRowIndices: Ref<number[]>;

  /**
   * Reactive selected row objects. Works in all selection modes.
   *
   * @since 2.5.0
   */
  selectedRows: Ref<TRow[]>;

  /**
   * Whether the grid has finished its first render and the selection plugin
   * has been discovered.
   *
   * @since 2.5.0
   */
  isReady: Ref<boolean>;
}

/**
 * Composable for programmatic selection control.
 *
 * Must be used within a component that contains a TbwGrid with the selection feature enabled.
 * Uses Vue's provide/inject, so it works reliably regardless of when the grid renders.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useGridSelection } from '@toolbox-web/grid-vue/features/selection';
 *
 * const { selectAll, clearSelection, getSelection } = useGridSelection();
 *
 * function exportSelected() {
 *   const selection = getSelection();
 *   if (!selection) return;
 *   // Derive rows from selection.ranges and grid.rows
 * }
 * </script>
 * ```
 * @param selector - Optional CSS selector to target a specific grid element via
 *   DOM query instead of using Vue's provide/inject. Use when the component
 *   contains multiple grids, e.g. `'tbw-grid.primary'` or `'#my-grid'`.
 */
export function useGridSelection<TRow = unknown>(selector?: string): SelectionMethods<TRow> {
  const gridElement = selector ? ref(null) : inject(GRID_ELEMENT_KEY, ref(null));

  const getGrid = (): DataGridElement<TRow> | null => {
    if (selector) return document.querySelector(selector) as DataGridElement<TRow> | null;
    return gridElement.value as DataGridElement<TRow> | null;
  };

  const getPlugin = (): SelectionPlugin | undefined => {
    return getGrid()?.getPluginByName('selection');
  };

  // ── Reactive state (parity with Angular's selection signals) ──────────
  const isReady = ref(false) as Ref<boolean>;
  const selection = ref<SelectionResult | null>(null) as Ref<SelectionResult | null>;
  const selectedRowIndices = ref<number[]>([]) as Ref<number[]>;
  const selectedRows = ref<TRow[]>([]) as Ref<TRow[]>;
  let unsubSelectionChange: (() => void) | undefined;
  let disposed = false;

  // `mode` may be a single mode or an array (multi-mode selection).
  const sync = (mode?: SelectionChangeDetail['mode']): void => {
    const plugin = getPlugin();
    if (!plugin || disposed) return;
    const resolvedMode = mode ?? ((plugin as any).config?.mode as SelectionChangeDetail['mode'] | undefined);
    const isRowMode = Array.isArray(resolvedMode) ? resolvedMode.includes('row') : resolvedMode === 'row';
    selection.value = plugin.getSelection();
    selectedRowIndices.value = isRowMode ? plugin.getSelectedRowIndices() : [];
    selectedRows.value = plugin.getSelectedRows<TRow>();
  };

  onMounted(() => {
    const grid = getGrid();
    if (!grid) return;

    unsubSelectionChange = grid.on?.('selection-change', (detail: unknown) => {
      sync((detail as SelectionChangeDetail).mode);
    });

    // `ready` is optional on the element type and genuinely absent until the
    // custom element upgrades, so unwrap through `Promise.resolve`.
    Promise.resolve(grid.ready?.()).then(() => {
      if (disposed) return;
      isReady.value = true;
      sync();
    });
  });

  onBeforeUnmount(() => {
    disposed = true;
    unsubSelectionChange?.();
    unsubSelectionChange = undefined;
  });

  return {
    isReady,
    selection,
    selectedRowIndices,
    selectedRows,
    selectAll: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:selection] SelectionPlugin not found.\n\n` +
            `  → Enable selection on the grid:\n` +
            `    <TbwGrid selection="range" />`,
        );
        return;
      }
      const grid = getGrid();
      // Cast to any to access protected config
      const mode = (plugin as any).config?.mode;

      if (mode === 'row') {
        const rowCount = grid?.rows?.length ?? 0;
        const allIndices = new Set<number>();
        for (let i = 0; i < rowCount; i++) allIndices.add(i);
        (plugin as any).selected = allIndices;
        (plugin as any).requestAfterRender?.();
      } else if (mode === 'range') {
        const rowCount = grid?.rows?.length ?? 0;
        const colCount = (grid as any)?._columns?.length ?? 0;
        if (rowCount > 0 && colCount > 0) {
          plugin.setRanges([{ from: { row: 0, col: 0 }, to: { row: rowCount - 1, col: colCount - 1 } }]);
        }
      }
    },

    clearSelection: () => {
      getPlugin()?.clearSelection();
    },

    getSelection: () => {
      return getPlugin()?.getSelection() ?? null;
    },

    isCellSelected: (row: number, col: number) => {
      return getPlugin()?.isCellSelected(row, col) ?? false;
    },

    setRanges: (ranges: CellRange[]) => {
      getPlugin()?.setRanges(ranges);
    },

    getSelectedRows: (): TRow[] => {
      return getPlugin()?.getSelectedRows<TRow>() ?? [];
    },
  };
}
